<template>
  <h3 class="tw:mt-4">
    {{ $t("modals.saml_integration_modal.user_role_mappings") }}
  </h3>
  <div class="tw:mt-4">
    {{ $t("modals.saml_integration_modal.user_role_mappings_description") }}
    <br />
    <span class="tw:font-bold">
      {{ $t("modals.saml_integration_modal.note") }}
    </span>
    <span>
      {{
        $t("modals.saml_integration_modal.user_role_mappings_note_description")
      }}
    </span>
  </div>

  <div>
    <p class="tw:mt-4">
      {{ $t("modals.saml_integration_modal.add_user_role") }}
    </p>
    <div class="tw:mb-4 tw:grid tw:grid-cols-[1fr_1fr_0.1fr] tw:gap-2">
      <InputText
        v-model="selectedOptions.customAttribute"
        new-design
        :placeholder="
          $t('modals.saml_integration_modal.custom_attribute_value_placeholder')
        "
      />

      <InputSelect
        v-model="selectedOptions.selectedRole"
        new-design
        option-label="name"
        :options="groupedRoles"
        :placeholder="$t('modals.saml_integration_modal.select_user_role')"
      >
        <template #option="{ option }">
          <div
            :class="{
              'tw:font-bold': option.group,
              'tw:cursor-pointer': !option.group,
            }"
          >
            <div
              class="tw:p-2"
              :class="optionClasses(option)"
            >
              {{ option.name }}
            </div>
          </div>
        </template>
      </InputSelect>

      <BaseButton
        color="ds-denim-3"
        :label="$t('global.add')"
        @click="addCustomAttribute"
      />
    </div>
    <div
      v-for="(roles, groupName) in samlIntegration.customAttributes?.roles"
      :key="groupName"
    >
      <div v-if="roles.length">
        <div class="tw:mb-2 tw:font-bold tw:text-denim-700">
          {{ reverseUserRoleMappings[groupName].toUpperCase() }}
        </div>

        <div
          class="tw:mb-4 tw:rounded-lg tw:border tw:border-solid tw:border-denim-200"
        >
          <div
            class="tw:grid tw:grid-cols-[1fr_1fr_0.1fr] tw:items-center tw:p-2"
          >
            <div class="tw:font-bold tw:text-denim-700">
              CUSTOM ATTRIBUTE VALUE
            </div>
            <div class="tw:font-bold tw:text-denim-700">
              {{ reverseUserRoleMappings[groupName].toUpperCase() }} USER ROLE
            </div>
            <div />
          </div>
          <BaseSeparator />

          <div
            v-for="(role, key) in roles"
            :key="key"
            class="tw:grid tw:grid-cols-[1fr_1fr_0.1fr] tw:items-center tw:p-2"
          >
            <div class="text-gray-700">
              {{ role.attribute_name }}
            </div>
            <div class="font-medium text-gray-900">
              {{ role.name }}
            </div>

            <BaseButtonDropdown
              close-on-click
              flat
              icon="more_vert"
              menu-item
              new-dropdown
              new-icon
              padding="xs"
              round
            >
              <BaseButton
                icon="delete"
                :label="$t('global.delete')"
                new-design
                @click="onRemoveRole(String(groupName), role)"
              />
            </BaseButtonDropdown>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from "vue";

import { ErrorDialog } from "shared/boot/alert";
import { getLocaleText } from "shared/boot/i18n";
import {
  BaseButton,
  BaseButtonDropdown,
  BaseSeparator,
  InputSelect,
  InputText,
} from "shared/components/base";
import { constantize } from "shared/helpers/string";
import type { SamlIntegration } from "shared/resources";
import { Role } from "shared/resources";

// TODO: TS was being hard on me with the different types of roles transformations
// I used any for everything, it would be good to update this at some point
const samlIntegration = defineModel<SamlIntegration>({
  required: true,
});

const selectedOptions = ref<SelectedOptions>({
  customAttribute: "",
  selectedRole: {} as any,
});

interface SelectedOptions {
  customAttribute: string;
  selectedRole: any;
}
const groupedRoles = ref({} as any);

const userRoleMappings = {
  traditional: "monitoring_user_role",
  social: "social_user_role",
  outreach: "outreach_user_role",
};

const reverseUserRoleMappings = Object.fromEntries(
  Object.entries(userRoleMappings).map(([key, value]) => [
    value,
    constantize(key),
  ])
);

async function loadRoles() {
  try {
    const roles = (await Role.all()).data;

    groupedRoles.value = roles.reduce<{ product: string; roles: any[] }[]>(
      (acc, role) => {
        const { product } = role;
        let group = acc.find((listGroup) => listGroup.product === product);

        if (!group) {
          group = { product, roles: [] };
          acc.push(group);
        }

        group.roles.push(role);

        return acc;
      },
      []
    );

    groupedRoles.value = groupedRoles.value.flatMap((groupedRole: any) => [
      { name: groupedRole.product.toUpperCase(), group: true },
      ...groupedRole.roles.map((groupRole: Role) => ({
        id: groupRole.id,
        name: constantize(groupRole.name),
        product: groupedRole.product,
        group: false,
      })),
    ]);
  } catch (error) {
    ErrorDialog(
      getLocaleText("saml_integration.load_integration_error"),
      error
    );
  }
}

function addCustomAttribute() {
  const { value: saml } = samlIntegration;
  const { selectedRole, customAttribute } = selectedOptions.value;
  // @ts-expect-error lacks typing
  const roleKey = userRoleMappings[selectedRole.product];

  // Ensure the structure exists
  saml.customAttributes = saml.customAttributes || {};
  saml.customAttributes.roles = saml.customAttributes.roles || {};

  saml.customAttributes.roles[roleKey] =
    saml.customAttributes.roles[roleKey] || [];

  saml.customAttributes.roles[roleKey].push({
    attribute_name: customAttribute,
    id: selectedRole.id,
    name: selectedRole.name,
  });

  selectedOptions.value.customAttribute = "";
  selectedOptions.value.selectedRole = {} as Role;
}

function optionClasses(option: any) {
  if (option.group) {
    return "";
  }

  return "tw:mx-4 tw:hover:bg-gray-100";
}

function onRemoveRole(groupName: string, role: any) {
  const { value: saml } = samlIntegration;

  saml.customAttributes.roles[groupName] = saml.customAttributes.roles[
    groupName
  ].filter((listRole: any) => listRole.id !== role.id);
}

onMounted(() => {
  loadRoles();
});
</script>
